/**
 * LandingFirmy - Landing page dla firm usługowych
 * /dla-firm-uslugowych
 * 
 * FINAL / APPROVED / DO NOT CHANGE (content)
 */
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  Building2, Briefcase, Users, TrendingUp, Check, Star, 
  ArrowRight, Clock, Shield, Zap, MessageSquare,
  Calendar, Award, Phone, Mail, Globe, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ROICalculator from '@/components/ROICalculator';
import { useTranslation } from 'react-i18next';

// Animated section wrapper
function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.section
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.section>
  );
}

export default function LandingFirmy() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white">
      
      {/* ==================== HERO ==================== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#EEF2FF] via-white to-[#F0FDF4] pt-20 pb-24">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          {/* Nav */}
          <nav className="flex items-center justify-between mb-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.svg" alt="ApisAI" className="w-10 h-10" />
              <span className="font-bold text-xl text-gray-900">ApisAI</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Zaloguj się
              </Link>
              <Link to="/register">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                  Załóż konto
                </Button>
              </Link>
            </div>
          </nav>

          {/* Hero content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full text-orange-700 text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                Dla firm usługowych
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Strona, która pracuje na Twoje zapytania.{' '}
                <span className="text-orange-600">Marketing bez chaosu.</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 max-w-xl">
                Profesjonalna strona firmy + automatyczny marketing. 
                Zdobywaj klientów bez agencji, bez stresu, bez tracenia czasu.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-8">
                    Załóż stronę za darmo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/demo-panel">
                  <Button size="lg" variant="outline" className="border-gray-300">
                    Zobacz demo
                  </Button>
                </Link>
              </div>
              
              {/* Trust badges */}
              <div className="flex items-center gap-6 mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm text-gray-600">Bez karty kredytowej</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-600">Gotowe w 15 minut</span>
                </div>
              </div>
            </div>
            
            {/* Hero image/illustration - Mockup strony firmy */}
            <div className="relative hidden lg:block">
              <div className="relative bg-white rounded-3xl shadow-2xl shadow-gray-200/50 p-4 border border-gray-100 overflow-hidden">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-lg px-3 py-1 text-xs text-gray-500 text-center">
                    salon-elegance.apisai.pl
                  </div>
                </div>
                
                {/* Page mockup */}
                <div className="bg-gradient-to-br from-orange-50 to-purple-50 rounded-xl overflow-hidden" style={{ height: '320px' }}>
                  {/* Header */}
                  <div className="bg-white/80 backdrop-blur px-4 py-3 flex items-center justify-between border-b border-orange-100">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600" />
                      <span className="font-bold text-gray-800 text-sm">Salon Elegance</span>
                    </div>
                    <div className="flex gap-4 text-xs text-gray-600">
                      <span>Usługi</span>
                      <span>Cennik</span>
                      <span>Galeria</span>
                      <span className="text-orange-600 font-semibold">Umów wizytę</span>
                    </div>
                  </div>
                  
                  {/* Hero section */}
                  <div className="p-4">
                    <div className="flex gap-4">
                      {/* Main image placeholder */}
                      <div className="w-48 h-32 rounded-xl bg-gradient-to-br from-orange-200 to-purple-200 flex items-center justify-center overflow-hidden">
                        <Building2 className="w-16 h-16 text-orange-600/50" />
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-bold text-gray-800 mb-1">Salon Kosmetyczny Elegance</div>
                        <div className="text-xs text-gray-500 mb-2">Kraków • ul. Floriańska 15</div>
                        <div className="flex gap-1 mb-2">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                          ))}
                          <span className="text-xs text-gray-500 ml-1">87 opinii</span>
                        </div>
                        <div className="bg-orange-600 text-white text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Umów wizytę
                        </div>
                      </div>
                    </div>
                    
                    {/* Services section */}
                    <div className="mt-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Popularne usługi</div>
                      <div className="flex gap-2">
                        {[
                          { name: 'Manicure', price: '80 zł' },
                          { name: 'Pedicure', price: '100 zł' },
                          { name: 'Masaż', price: '150 zł' }
                        ].map((service, i) => (
                          <div key={i} className="flex-1 bg-white rounded-lg p-2 shadow-sm">
                            <div className="w-full h-10 rounded bg-gradient-to-br from-orange-100 to-purple-100 mb-1.5 flex items-center justify-center">
                              <Zap className="w-4 h-4 text-orange-400" />
                            </div>
                            <div className="text-[10px] font-medium text-gray-700">{service.name}</div>
                            <div className="text-[9px] text-orange-600 font-semibold">{service.price}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating cards */}
                <motion.div 
                  className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-lg p-4 border border-gray-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Nowe zapytanie</p>
                      <p className="text-xs text-gray-500">2 minuty temu</p>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="absolute -right-4 -bottom-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">+28% więcej zapytań</p>
                      <p className="text-xs text-gray-500">vs poprzedni miesiąc</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PROBLEM ==================== */}
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Brzmi znajomo?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Prowadzisz firmę, ale marketing to chaos. Nie wiesz co działa, 
              na co wydajesz i czy w ogóle ma to sens.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Globe,
                title: "Strona, której nikt nie odwiedza",
                description: "Masz stronę, ale stoi pusta. Klienci nie przychodzą z internetu – tylko z polecenia."
              },
              {
                icon: MessageSquare,
                title: "Marketing = chaos i strata czasu",
                description: "Posty na Facebooku, wizytówka Google, reklamy… nic nie działa razem."
              },
              {
                icon: Briefcase,
                title: "Agencje drogo, samemu nie umiesz",
                description: "Agencja chce 3000 zł miesięcznie. Sam nie masz czasu ani wiedzy."
              }
            ].map((problem, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                  <problem.icon className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{problem.title}</h3>
                <p className="text-gray-600 text-sm">{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ==================== ROZWIĄZANIE ==================== */}
      <AnimatedSection className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              ApisAI – marketing bez chaosu
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Profesjonalna strona + automatyczny marketing = klienci, którzy sami do Ciebie trafiają.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Globe,
                title: "Strona firmy w 15 minut",
                description: "Kreator prowadzi Cię krok po kroku. Bez technikaliów, bez kodowania.",
                color: "bg-orange-50 text-orange-600"
              },
              {
                icon: Zap,
                title: "Automatyczne publikacje",
                description: "Booster publikuje posty w grupach branżowych. Codziennie, bez Twojego udziału.",
                color: "bg-amber-50 text-amber-600"
              },
              {
                icon: Users,
                title: "Zapytania w jednym miejscu",
                description: "CRM zbiera wszystkie zapytania. Widzisz kto pytał, kiedy i o co.",
                color: "bg-emerald-50 text-emerald-600"
              },
              {
                icon: Target,
                title: "Marketing, który mierzysz",
                description: "Dashboard pokazuje co działa. Wiesz ile zapytań dostałeś i skąd.",
                color: "bg-blue-50 text-blue-600"
              },
              {
                icon: Calendar,
                title: "Oszczędność czasu",
                description: "Zamiast godzin na marketing – 15 minut tygodniowo na przegląd wyników.",
                color: "bg-pink-50 text-pink-600"
              },
              {
                icon: Shield,
                title: "Bez ryzyka",
                description: "Zacznij za darmo. Płacisz tylko gdy widzisz, że to działa.",
                color: "bg-gray-100 text-gray-600"
              }
            ].map((feature, i) => (
              <motion.div 
                key={i}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ==================== JAK TO DZIAŁA ==================== */}
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              3 kroki do pierwszych klientów
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Załóż stronę",
                description: "Kreator prowadzi Cię krok po kroku. Opisz firmę, dodaj usługi, cennik."
              },
              {
                step: "2",
                title: "Włącz Booster",
                description: "Wybierz grupy branżowe, gdzie Booster będzie publikował. Ustaw częstotliwość."
              },
              {
                step: "3",
                title: "Odbieraj zapytania",
                description: "Zapytania trafiają do CRM. Odpowiadasz, umawiasz, zamykasz sprzedaż."
              }
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-orange-600 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ==================== DLA KOGO ==================== */}
      <AnimatedSection className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Dla jakich firm?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              ApisAI działa najlepiej dla firm usługowych, które polegają na lokalnych klientach.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "Salony kosmetyczne",
              "Warsztaty samochodowe", 
              "Firmy remontowe",
              "Fotografowie",
              "Trenerzy personalni",
              "Firmy sprzątające",
              "Gabinety masażu",
              "I wiele innych..."
            ].map((industry, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
                <span className="text-gray-700 font-medium">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ==================== KALKULATOR ROI ==================== */}
      <AnimatedSection className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <ROICalculator type="services" accentColor="orange" />
        </div>
      </AnimatedSection>

      {/* ==================== ZAUFANIE ==================== */}
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Co mówią nasi klienci
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "Pierwszego klienta z internetu dostałem po 3 dniach. Wcześniej przez pół roku nic. ApisAI to zmienił.",
                author: "Tomasz R.",
                role: "Firma remontowa"
              },
              {
                quote: "Agencja chciała 3500 zł miesięcznie. ApisAI kosztuje ułamek tego i działa lepiej. Polecam.",
                author: "Monika S.",
                role: "Salon kosmetyczny"
              },
              {
                quote: "Wreszcie wiem skąd przychodzą klienci. Dashboard pokazuje wszystko. Zero zgadywania.",
                author: "Paweł K.",
                role: "Fotograf"
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map(j => (
                    <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ==================== CTA ==================== */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Zacznij zdobywać klientów z internetu
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Dołącz do firm, które automatyzują swój marketing i mają więcej czasu na to, co robią najlepiej.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-8">
                Załóż stronę za darmo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          
          <p className="text-white/60 text-sm mt-6">
            Bez karty kredytowej • Gotowe w 15 minut
          </p>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-12 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="ApisAI" className="w-8 h-8" />
              <span className="font-semibold">ApisAI</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white">Polityka prywatności</Link>
              <Link to="/terms" className="hover:text-white">Regulamin</Link>
              <a href="https://wa.me/48507334540?text=Hej%2C%20mam%20pytanie" target="_blank" rel="noopener noreferrer" className="hover:text-white flex items-center gap-1">
                <Mail className="w-4 h-4" /> WhatsApp
              </a>
            </div>
            
            <p className="text-sm text-gray-500">
              © 2026 ApisAI. Wszystkie prawa zastrzeżone.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
